import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';

const WOCHENTAGE = [1, 2, 3, 4, 5, 6, 7];

const TYPEN = {
  ARBEITSZEIT_OFFICE: { cls: 'bg-primary', icon: 'bi-building' },
  ARBEITSZEIT_HOME_OFFICE: { cls: 'bg-info text-dark', icon: 'bi-house' },
  URLAUB_GENEHMIGT: { cls: 'bg-success', icon: 'bi-check-circle' },
  URLAUB_OFFEN: { cls: 'bg-warning text-dark', icon: 'bi-hourglass-split' },
  ABWESENHEIT: { cls: 'bg-danger', icon: 'bi-bandaid' },
  ABWESENHEIT_FZT: { cls: 'badge-fzt', icon: 'bi-calendar-check' },
  VERTRETUNG: { cls: 'bg-secondary', icon: 'bi-people' },
};

const iso = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const t = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${t}`;
};

const montag = (d) => {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  return r;
};

const addTage = (d, n) => {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + n);
  return r;
};

const typKey = (e) => {
  if (e.typ === 'ARBEITSZEIT') return `ARBEITSZEIT_${e.work_location}`;
  if (e.typ === 'ABWESENHEIT' && e.label === 'FZT') return 'ABWESENHEIT_FZT';
  return e.typ;
};

const fmtZeit = (s, locale) => (s ? new Date(s).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '…');

function eintragText(e, t, locale) {
  if (e.typ === 'ARBEITSZEIT') {
    return `${fmtZeit(e.check_in, locale)}–${fmtZeit(e.check_out, locale)}`;
  }
  return e.label || (TYPEN[typKey(e)] ? t(`calendar.type.${typKey(e)}`) : undefined);
}

export default function CalendarView() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [ansicht, setAnsicht] = useState('month');
  const [anker, setAnker] = useState(new Date());
  const [teamId, setTeamId] = useState('');
  const [nurEigene, setNurEigene] = useState(false);
  const [teams, setTeams] = useState([]);
  const [daten, setDaten] = useState({ users: [], entries: [] });
  const [fehler, setFehler] = useState('');

  const zeitraum = useMemo(() => {
    if (ansicht === 'week') {
      const start = montag(anker);
      return { start, end: addTage(start, 6) };
    }
    const erster = new Date(anker.getFullYear(), anker.getMonth(), 1);
    const letzter = new Date(anker.getFullYear(), anker.getMonth() + 1, 0);
    return { start: montag(erster), end: addTage(montag(letzter), 6) };
  }, [ansicht, anker]);

  useEffect(() => {
    api.get('/calendar/teams').then((r) => setTeams(r.data)).catch(() => setTeams([]));
  }, []);

  useEffect(() => {
    const params = { start: iso(zeitraum.start), end: iso(zeitraum.end), own_only: nurEigene };
    if (teamId) params.team_id = teamId;
    api.get('/calendar', { params })
      .then((r) => { setDaten(r.data); setFehler(''); })
      .catch((err) => {
        setDaten({ users: [], entries: [] });
        setFehler(err.response?.data?.detail || t('calendar.error.load'));
      });
          }, [zeitraum, teamId, nurEigene, t]);

  const tage = useMemo(() => {
    const liste = [];
    for (let d = zeitraum.start; d <= zeitraum.end; d = addTage(d, 1)) liste.push(d);
    return liste;
  }, [zeitraum]);

  const eintraegeAm = (tag) => {
    const s = iso(tag);
    return daten.entries.filter((e) => e.start_date <= s && e.end_date >= s);
  };

  const gruppiereTag = (eintraege) => {
    const gruppen = new Map();
    eintraege.forEach((e) => {
      const key = `${e.user_id ?? e.user_name}|${typKey(e)}`;
      if (!gruppen.has(key)) gruppen.set(key, []);
      gruppen.get(key).push(e);
    });
    return [...gruppen.values()];
  };

  const navigieren = (richtung) => {
    if (ansicht === 'week') setAnker(addTage(anker, 7 * richtung));
    else setAnker(new Date(anker.getFullYear(), anker.getMonth() + richtung, 1));
  };

  const titel = ansicht === 'week'
    ? `${zeitraum.start.toLocaleDateString(locale)} – ${zeitraum.end.toLocaleDateString(locale)}`
    : anker.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  const zeigeName = daten.users.length > 1 || user?.id !== daten.users[0]?.id;

  return (
    <div>
      <h2 className="mb-3"><i className="bi bi-calendar3 me-2" />{t('calendar.title')}</h2>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <div className="btn-group">
            <button className="btn btn-outline-secondary" onClick={() => navigieren(-1)}>
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-outline-secondary" onClick={() => setAnker(new Date())}>{t('calendar.today')}</button>
            <button className="btn btn-outline-secondary" onClick={() => navigieren(1)}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
          <strong className="fs-5">{titel}</strong>
          <div className="btn-group ms-auto">
            <button className={`btn ${ansicht === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnsicht('month')}>{t('calendar.month')}</button>
            <button className={`btn ${ansicht === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setAnsicht('week')}>{t('calendar.week')}</button>
          </div>
          <select className="form-select w-auto" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">{t('calendar.allTeams')}</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div className="form-check">
            <input id="nurEigene" className="form-check-input" type="checkbox" checked={nurEigene}
              onChange={(e) => setNurEigene(e.target.checked)} />
            <label htmlFor="nurEigene" className="form-check-label">{t('calendar.ownOnly')}</label>
          </div>
        </div>
      </div>

      {fehler && <div className="alert alert-danger">{fehler}</div>}

      <div className="mb-3 d-flex flex-wrap gap-2">
        {Object.entries(TYPEN).map(([k, typ]) => (
                  <span key={k} className={`badge ${typ.cls}`}><i className={`bi ${typ.icon} me-1`} />{t(`calendar.type.${k}`)}</span>
        ))}
      </div>

      <div className="table-responsive">
        <table className="table table-bordered" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>{WOCHENTAGE.map((w) => <th key={w} className="text-center">{t(`calendar.weekday.${w}`)}</th>)}</tr>
          </thead>
          <tbody>
            {Array.from({ length: tage.length / 7 }, (_, woche) => (
              <tr key={woche}>
                {tage.slice(woche * 7, woche * 7 + 7).map((tag) => {
                  const fremderMonat = ansicht === 'month' && tag.getMonth() !== anker.getMonth();
                  const heute = iso(tag) === iso(new Date());
                  return (
                    <td key={iso(tag)} className={fremderMonat ? 'bg-light text-muted' : ''}
                      style={{ verticalAlign: 'top', height: ansicht === 'week' ? 280 : 110,
                        outline: heute ? '2px solid #0d6efd' : 'none' }}>
                      <div className="small fw-bold mb-1">                      {tag.toLocaleDateString(locale, { day: 'numeric', month: 'numeric' })}</div>
                      {(ansicht === 'month' ? gruppiereTag(eintraegeAm(tag)) : eintraegeAm(tag).map((e) => [e])).map((gruppe, i) => {
                                              const e = gruppe[0];
                                              const typ = TYPEN[typKey(e)];
                                              const typLabel = typ ? t(`calendar.type.${typKey(e)}`) : undefined;
                                              return (
                                                <div key={i} className={`badge ${typ?.cls} d-block text-start text-wrap mb-1`}
                                                  title={`${e.user_name}: ${typLabel}${e.label ? ` – ${e.label}` : ''}`}>
                                                  <i className={`bi ${typ?.icon} me-1`} />
                                                  {zeigeName && <span>{e.user_name}: </span>}
                                                  {gruppe.length > 1 ? (
                                                    <>
                                                      <strong>{typLabel}</strong>
                                                      <span className="ms-1">({t('calendar.grouped.count', { n: gruppe.length })})</span>
                                                      <ul className="list-unstyled mb-0 ps-3">
                                                        {gruppe.map((x, j) => <li key={j}>{eintragText(x, t, locale)}</li>)}
                                                      </ul>
                                                    </>
                                                  ) : eintragText(e, t, locale)}
                          </div>
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
